import moment from "moment";
import { addTrailingZeros } from "@js-app/services/helpers/math";
import { BillInfo } from "@js-app/entities/transaction/BillInfo";
import {
  BILL_BY_TEMPLATE,
  CARD_INCOME,
  CREATED_BILL,
  PAID_BILL,
} from "@js-app/const/operation-types-ids";

export class Transaction {
  transactionData = {};
  billInfo = {};

  fullInfoLoaded = false;

  constructor(transactionData = {}) {
    this.transactionData = transactionData;
    this.billInfo = new BillInfo(this.transactionData.BillInfo);
  }

  /**
   *
   * @returns BillInfo
   */
  get billInfo() {
    return this.billInfo;
  }

  /**
   *
   * @returns {string}
   */
  get transactionDate() {
    try {
      return moment(this.transactionData.OperationDate).format("DD.MM.YYYY");
    } catch (e) {
      return "";
    }
  }

  /**
   *
   * @returns {string}
   */
  get operationDate() {
    try {
      return this.transactionData.OperationDate;
    } catch (e) {
      return "";
    }
  }

  /**
   *
   * @returns {string}
   */
  get transactionTime() {
    try {
      return moment(this.transactionData.OperationDate).format("HH:mm:ss");
    } catch (e) {
      return "";
    }
  }

  get transactionDateTime() {
    try {
      return moment(this.transactionData.OperationDate).format(
        "YYYY.MM.DD, HH:mm:ss"
      );
    } catch (e) {
      return "";
    }
  }

  /**
   *
   * @returns {*|string}
   */
  get transactionAmount() {
    try {
      return addTrailingZeros(
        (parseInt(this.transactionData.OperationSum) / 100).toLocaleString('uk-UA')
      );
    } catch (e) {
      return "";
    }
  }

  get transactionCoinAmount() {
    try {
      return addTrailingZeros(
          (parseInt(this.transactionData.OperationSum) / 100).toLocaleString('uk-UA')
      );
    } catch (e) {
      return "";
    }
  }



  /***
   *
   * @returns {number}
   */
  get transactionAmountFloat() {
    try {
      return parseInt(this.transactionData.OperationSum) / 100;
    } catch (e) {
      return 0;
    }
  }

  get transactionPurpose() {
    try {
        return this.transactionData.Purpose
    } catch (e) {
      return '';
    }
  }

  /**
   *
   * @returns {*|string}
   */
  get transactionAmountPlain() {
    try {
      return this.transactionData.OperationSum;
    } catch (e) {
      return "";
    }
  }

  get transactionCommission() {
    try {
      return addTrailingZeros(
        (parseInt(this.transactionData.CommisionSum) / 100).toLocaleString()
      );
    } catch (e) {
      return "";
    }
  }

  get transactionCommissionPlain() {
    try {
      return this.transactionData.CommisionSum;
    } catch (e) {
      return "";
    }
  }

  /**
   *
   * @returns {string}
   */
  get transactionCurrency() {
    try {
      if (this.transactionData.TransactionCurrency === "UAH") {
        return "₴";
      } else {
        return "€";
      }
    } catch (e) {
      return "";
    }
  }

  get transactionCurrencyValue() {
    try {
      switch (this.transactionData.TransactionCurrency) {
        case "UAH":
          return "₴";
        case "USD":
          return "$";
        case "EUR":
          return "€";
        default:
          return "";
      }
    } catch (e) {
      return "";
    }
  }

  get transactionCurrencyPlainValue() {
    try {
      switch (this.transactionData.TransactionCurrency) {
        case "UAH":
          return "UAH";
        case "USD":
          return "USD";
        case "EUR":
          return "EUR";
        default:
          return "";
      }
    } catch (e) {
      return "";
    }
  }

  /**
   *
   * @returns {string}
   */
  get transactionCurrencyPlain() {
    try {
      return this.transactionData.TransactionCurrency;
    } catch (e) {
      return "";
    }
  }

  /**
   *
   * @returns {null|*}
   */
  get operationId() {
    try {
      return this.transactionData.OperationID;
    } catch (e) {
      return null;
    }
  }

  /**
   *
   * @returns {null|*}
   */
  get partnerTransactionId() {
    try {
      return this.transactionData.PartnerTransactionID;
    } catch (e) {
      return null;
    }
  }

  /**
   *
   * @returns {null|*}
   */
  get terminalId() {
    try {
      return this.transactionData.PartnerTerminalID;
    } catch (e) {
      return null;
    }
  }

  /**
   *
   * @returns {null|*}
   */
  get orderNumber() {
    try {
      return this.transactionData.Order?.trim().length === 0
        ? this.transactionData?.BillInfo?.order ?? ""
        : this.transactionData.Order;
    } catch (e) {
      return null;
    }
  }

  /**
   *
   * @returns {null|*}
   */
  get operationTypeId() {
    try {
      return this.transactionData.OperationTypeID;
    } catch (e) {
      return null;
    }
  }

  /**
   *
   * @returns {string|*|string}
   */
  get transactionType() {
    try {
      if ([6003, 6004].includes(parseInt(this.operationTypeId))) {
        return parseInt(this.operationTypeId);
      }

      return this.transactionData.TransactionType || "none";
    } catch (e) {
      return "none";
    }
  }

  /**
   *
   * @returns {{firstname: string, middlename: string, lastname: string, toString: (function(): string)}}
   */
  get userFullName() {
    try {
      let _fullNameParts = this.transactionData.UserFullName.split(" ");

      let _lastName = _fullNameParts[0] || "";

      if (_lastName.length > 2) {
        let _lastNameFirstChar = _lastName[0],
          _lastNameLastChar = _lastName[_lastName.length - 1];

        _lastName =
          _lastNameFirstChar +
          "".padEnd(_lastName.length, "*") +
          _lastNameLastChar;
      } else if (_lastName.length > 0 && _lastName.length <= 2) {
        let _lastNameFirstChar = _lastName[0];

        _lastName = _lastNameFirstChar + "".padEnd(2, "*");
      }

      return {
        firstname: _fullNameParts[1] || "",
        lastname: _lastName,
        middlename: _fullNameParts[2] || "",
        toString: () =>
          [_lastName, _fullNameParts[1] || "", _fullNameParts[2] || ""].join(
            " "
          ),
      };
    } catch (e) {
      return {
        firstname: "",
        lastname: "",
        middlename: "",
        toString: () => this.transactionData.UserFullName,
      };
    }
  }

  /**
   *
   * @returns {number}
   */
  get operationStatus() {
    try {
      return parseInt(this.transactionData.OperationStatus);
    } catch (e) {
      return 0;
    }
  }

  /**
   *
   * @returns {boolean}
   */
  get canGetReceipt() {
    try {
      return !!this.transactionData.ExistTpl;
    } catch (e) {
      return false;
    }
  }

  /**
   *
   * @returns {boolean}
   */
  get canMakeRefund() {
    try {
      return !!this.transactionData.CanRefund;
    } catch (e) {
      return false;
    }
  }

  /**
   *
   * @returns {boolean}
   */
  get canCancelPreAuth() {
    try {
      return !!this.transactionData?.PreAuthId;
    } catch (e) {
      return false;
    }
  }

  /**
   *
   * @returns {string|*}
   */
  get cardNumber() {
    try {
      return this.transactionData.Card;
    } catch (e) {
      return "";
    }
  }

  get recipientAccount() {
    try {
      return this.transactionData.RecipientAccount;
    } catch (e) {
      return "";
    }
  }

  get recipientName() {
    try {
      return this.transactionData.RecipientName;
    } catch (e) {
      return "";
    }
  }

  get rrn() {
    try {
      return this.transactionData.RRN;
    } catch (e) {
      return "";
    }
  }

  get browserIp() {
    try {
      return this.transactionData.BrowserIP;
    } catch (e) {
      return "";
    }
  }

  get bankName() {
    try {
      return this.transactionData.BankName;
    } catch (e) {
      return "";
    }
  }

  get payType() {
    try {
      return this.transactionData.PayType;
    } catch (e) {
      return "";
    }
  }

  get paySystem() {
    try {
      return this.transactionData.PaySystem;
    } catch (e) {
      return "";
    }
  }

  get userEmail() {
    try {
      return this.transactionData.UserEmail;
    } catch (e) {
      return "";
    }
  }

  get ipn() {
    try {
      return this.transactionData?.RecipientIPN || "";
    } catch (e) {
      return "";
    }
  }

  get userPhone() {
    try {
      return this.transactionData.UserCellPhone.replace("380", "0");
    } catch (e) {
      return "";
    }
  }

  get userPhoneEmail() {
    try {
      if (this.transactionData.UserCellPhone.length > 0) {
        return this.transactionData.UserCellPhone;
      } else {
        return this.transactionData.UserEmail;
      }
    } catch (e) {
      return "";
    }
  }

  /**
   *
   * @returns {array}
   */
  get paymentInfo() {
    try {
      return this.transactionData.PaymentInfo;
    } catch (e) {
      return [];
    }
  }

  get refunds() {
    try {
      return this.transactionData.Refunds || [];
    } catch (e) {
      return [];
    }
  }

  /**
   *
   * @returns {{msg: string, code: string}}
   */
  get error() {
    try {
      return {
        msg: this.transactionData.ErrMsg,
        code: this.transactionData.ErrCode,
      };
    } catch (e) {
      return { msg: "", code: "" };
    }
  }

  applyFullInfo(transactionData) {
    this.transactionData = transactionData;
    this.billInfo = new BillInfo(this.transactionData.BillInfo || {});
    this.fullInfoLoaded = true;
  }

  get TRANSACTION_DETAIL_BLOCKS() {
    let detailBlockBase = [
      "TransactionItemTransactionInfo",
      "TransactionItemTransactionDetailsInfo",
      "TransactionItemCardClientInfo",
    ];

    if (
      [PAID_BILL, CREATED_BILL, BILL_BY_TEMPLATE].includes(this.operationTypeId)
    ) {
      return [
        ...detailBlockBase,
        "TransactionItemBillInfo",
        "TransactionItemChargebackInfo",
      ];
    } else if ([CARD_INCOME].includes(this.operationTypeId)) {
      return detailBlockBase;
    } else {
      return "*";
    }
  }

  get parentOperation() {
    return {
      operationId: this.transactionData.PanOperationId ?? "",
      terminalId: this.transactionData.PanOperationPartnerTerminalId ?? "",
      operationTransactionId:
        this.transactionData.PanOperationPartnerTransactionId ?? "",
      operationDt: this.transactionData.OanOperationDT ?? "",
    };
  }
}
